import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  FormLabel,
  Icon,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  GetReportsByCreditFacilityIdQueryGQLType,
  useGetReportsByCreditFacilityIdQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { useDownloadReportUrl } from '../hooks/useDownloadReportUrl';
import { FilterDatePicker } from 'packages/common-frontend/components/FilterDatePicker';
import { DownloadIconButton } from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { FiCheck } from 'react-icons/fi';

type ReportGqlType = GetReportsByCreditFacilityIdQueryGQLType['reports'][number];

type ReportRowProps = { report: ReportGqlType };
const ReportRow = ({ report }: ReportRowProps) => {
  const { reportName, reportType, reportingDate, version, isPreview } = report;

  const { getReportDownloadUrl, isLoading } = useDownloadReportUrl(report);

  return (
    <Tr>
      <Td>{reportName}</Td>
      <Td>{commonUtils.toTitleCase(reportType)}</Td>
      <Td textAlign="center">
        {isPreview ? <Icon aria-label="is preview" as={FiCheck} /> : '-'}
      </Td>
      <Td>{reportingDate}</Td>
      <Td>{version}</Td>
      <Td textAlign="center">
        <DownloadIconButton
          ariaLabel="download report"
          label="Download report"
          fontSize="xl"
          position="static"
          isLoading={isLoading}
          onClick={getReportDownloadUrl}
        />
      </Td>
    </Tr>
  );
};

const REPORT_STATES = ['Live', 'Preview'] as const;
type REPORT_STATE_TYPE = typeof REPORT_STATES[number];
const REPORT_OPTIONS = REPORT_STATES.map((state) => ({
  value: state,
  label: state,
}));
const previewLabel =
  'Preview reports are generated for internal review, and are not sent to the capital provider.';

type ServicerReportsTableProps = { creditFacilityId: string };
export const ServicerReportsTable = ({
  creditFacilityId,
}: ServicerReportsTableProps) => {
  const toast = useToast();
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [reportState, setReportState] = useState<REPORT_STATE_TYPE>('Live');

  // NOTE: this currently fetches all servicer reports for the creditFacility.
  // If this list size becomes very large (still far from this), then we can
  // consider updating this logic to incorporate pagination
  const { data: reportsData } = useGetReportsByCreditFacilityIdQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: { creditFacilityId },
  });

  const targetReports = useMemo<ReportGqlType[]>(() => {
    if (!reportsData) {
      return [];
    }

    return reportsData.reports.filter((report) => {
      const { reportingDate, isPreview } = report;

      // *** Reporting Date ***
      if (startDate) {
        const startDateStr = commonDate.dateToFullDateString(
          startDate,
          clientTimeZone,
        );
        if (reportingDate.localeCompare(startDateStr) === -1) {
          return false;
        }
      }
      if (endDate) {
        const endDateStr = commonDate.dateToFullDateString(
          endDate,
          clientTimeZone,
        );
        if (endDateStr.localeCompare(reportingDate) === -1) {
          return false;
        }
      }

      // *** Report State ***
      if (reportState === 'Live' && isPreview) {
        return false;
      }
      if (reportState === 'Preview' && !isPreview) {
        return false;
      }

      return true;
    });
  }, [clientTimeZone, endDate, reportState, reportsData, startDate]);

  if (!reportsData) {
    return (
      <Skeleton data-testid="loading-servicer-reports-table" height="600px" />
    );
  }

  if (reportsData.reports.length === 0) {
    return (
      <Alert status="info" mt="5">
        <AlertIcon />
        <AlertDescription>No reports are currently available</AlertDescription>
      </Alert>
    );
  }

  return (
    <Box>
      <Flex alignItems="center">
        <Flex alignItems="center" mr="8">
          <FormLabel mb="0" mr="2" fontWeight="normal">
            Report State:
          </FormLabel>
          <Box w="32">
            <Select<REPORT_STATE_TYPE>
              value={reportState}
              options={REPORT_OPTIONS}
              onChange={(field) => {
                if (field) {
                  setReportState(field);
                }
              }}
            />
          </Box>
        </Flex>

        <FilterDatePicker
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          label="Date Range:"
        />
      </Flex>

      {targetReports.length === 0 ? (
        <Alert status="info" mt="5">
          <AlertIcon />
          <AlertDescription>
            No reports per the utilized filters
          </AlertDescription>
        </Alert>
      ) : (
        <Box maxH="xl" overflowY="auto">
          <Table mt="3">
            <Thead position="sticky" top="0">
              <Tr>
                <Th>Name</Th>
                <Th>Report Type</Th>
                <Th textAlign="center">
                  Preview{' '}
                  <Tooltip hasArrow label={previewLabel}>
                    <InfoOutlineIcon ml="2px" />
                  </Tooltip>
                </Th>
                <Th>Reporting Date</Th>
                <Th>Version</Th>
                <Th textAlign="center">Download</Th>
              </Tr>
            </Thead>

            <Tbody>
              {targetReports.map((report) => (
                <ReportRow key={report.id} report={report} />
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
